// src/pages/SeasonPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import { ref, update, onValue } from "firebase/database";
import { subscribeLeague, getWeekSchedule } from "../services/leagueApi";
import { WeekPicker } from "../components/WeekPicker";
import { MatchupCard } from "../components/MatchupCard";
import { waiverWindowLabel } from "../services/waiverApi";
import { FANTASY_WEEKS, getCurrentFantasyWeek } from "../services/iplSchedule";

export function SeasonPage({ leagueCode }) {
  const [league, setLeague] = useState(null);
  const [room,   setRoom]   = useState(null);
  const [week,   setWeek]   = useState(1);
  const [weekStartStatus, setWeekStartStatus] = useState("");

  const uid     = auth.currentUser?.uid;
  const isHost  = league?.ownerUid === uid;

  useEffect(() => {
    return subscribeLeague(db, leagueCode, setLeague);
  }, [leagueCode]);

  useEffect(() => {
    if (!leagueCode) return;
    const unsub = onValue(ref(db, `rooms/${leagueCode.toUpperCase()}`), s => setRoom(s.val() || null));
    return () => unsub();
  }, [leagueCode]);

  const teamName = (slot) => room?.teamCustom?.[slot]?.name || room?.slots?.[slot]?.name || slot;

  useEffect(() => {
    if (league?.season?.week) setWeek(league.season.week);
  }, [league?.season?.week]);

  const scheduleWeek = useMemo(() => getWeekSchedule(league, week), [league, week]);

  const matchups = useMemo(() => {
    const ms = scheduleWeek?.matchups || [];
    return ms.map(m => ({
      home:       m.home       ?? m.homeTeam ?? "TBD",
      away:       m.away       ?? m.awayTeam ?? "TBD",
      homePoints: m.homePoints ?? null,
      awayPoints: m.awayPoints ?? null,
    }));
  }, [scheduleWeek]);

  const typeLabel = useMemo(() => {
    if (!scheduleWeek) return null;
    if (scheduleWeek.type === "semifinals") return "Playoffs · Semifinals";
    if (scheduleWeek.type === "final")      return "Playoffs · Grand Final";
    return "Regular Season";
  }, [scheduleWeek]);

  // Auto-set week start from IPL 2026 schedule
  const setWeekFromIPL = async (weekNum) => {
    const iplWeek = FANTASY_WEEKS.find(w => w.week === weekNum);
    if (!iplWeek) return;
    const code = leagueCode.toUpperCase();
    try {
      await update(ref(db), {
        [`leagues/${code}/season/weekStartTime`]: iplWeek.startTime,
        [`leagues/${code}/season/week`]: weekNum,
      });
      setWeekStartStatus(`✓ Set to IPL schedule: ${iplWeek.label}`);
    } catch (e) { setWeekStartStatus("Save failed."); }
  };

  // Advance to current real-world week
  const syncToCurrentWeek = async () => {
    const currentWeek = getCurrentFantasyWeek();
    await setWeekFromIPL(currentWeek);
    setWeek(currentWeek);
  };

  const waiverLabel  = league ? waiverWindowLabel(league) : "—";
  const teamPoints   = league?.season?.teamPoints?.[String(week)] || {};
  const sortedTeams  = Object.entries(teamPoints).sort((a, b) => b[1] - a[1]);
  const currentIPLWeek = getCurrentFantasyWeek();
  const savedWeekStart = league?.season?.weekStartTime;

  return (
    <div>
      {/* Header */}
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Season
            </div>
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 2 }}>
              League: {leagueCode}
              {typeLabel && (
                <span className="pill muted" style={{ marginLeft: 8 }}>{typeLabel}</span>
              )}
              <span style={{ marginLeft: 12, fontSize: 11, color: "var(--text-muted)" }}>
                Waiver: {waiverLabel}
              </span>
            </div>
          </div>
          <WeekPicker week={week} setWeek={setWeek} min={1} max={12} />
        </div>

        {/* Host: set week from IPL 2026 schedule */}
        {isHost && (
          <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <span className="label">IPL 2026 Week Schedule</span>
              <button className="primary" onClick={syncToCurrentWeek} style={{ fontSize: 11 }}>
                ⚡ Sync to Current Week (Wk {currentIPLWeek})
              </button>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {FANTASY_WEEKS.map(w => (
                <button key={w.week} className="mini" onClick={() => setWeekFromIPL(w.week)}
                  style={{ fontSize: 10, outline: w.week === currentIPLWeek ? "1.5px solid var(--gold)" : undefined }}>
                  {w.label}
                </button>
              ))}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
              ℹ Weeks overlap — a week starts when the first team plays their N-th match.
            </div>
            {savedWeekStart && (
              <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-dim)" }}>
                Current lock time: {new Date(savedWeekStart).toLocaleString()}
              </div>
            )}
            {weekStartStatus && (
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--green)" }}>{weekStartStatus}</div>
            )}
          </div>
        )}
      </div>

      {!scheduleWeek ? (
        <div className="panel" style={{ padding: 24, textAlign: "center" }}>
          <div style={{ color: "var(--text-dim)", marginBottom: 8 }}>
            No schedule for this week yet.
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            The host needs to finalize the draft to generate the season schedule.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {matchups.length === 0 ? (
            <div className="panel" style={{ padding: 20, textAlign: "center", color: "var(--text-dim)" }}>
              No matchups this week.
            </div>
          ) : (
            matchups.map((m, i) => (
              <MatchupCard key={`${m.home}-${m.away}-${i}`} matchup={{
                ...m,
                home: teamName(m.home),
                away: teamName(m.away),
              }} />
            ))
          )}
        </div>
      )}

      {/* Weekly scores leaderboard */}
      {sortedTeams.length > 0 && (
        <div className="panel" style={{ marginTop: 12 }}>
          <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>Week {week} · Points Leaderboard</div>
          </div>
          <div>
            {sortedTeams.map(([team, pts], i) => (
              <div key={team} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 16px",
                borderBottom: i < sortedTeams.length - 1 ? "1px solid var(--border)" : "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className={`rank-badge rank-${i + 1}`}>{i + 1}</div>
                  <span style={{ fontWeight: 600 }}>{teamName(team)}</span>
                </div>
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 20,
                  fontWeight: 700,
                  color: i === 0 ? "var(--gold)" : "var(--text)",
                }}>
                  {pts}
                  <span style={{ fontSize: 11, color: "var(--text-dim)", marginLeft: 4 }}>pts</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
